import { useEffect, useState } from "react";
import { http, apiError, nf } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Ui";
import UploadBox from "@/components/UploadBox";
import { Save } from "lucide-react";

const BRANCHES = ["Surabaya", "Jakarta", "Medan", "Semarang", "Bandung", "Palembang", "Pekanbaru", "Makassar", "Banjarmasin", "Balikpapan"];
const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export default function SalesEntry() {
  const [f, setF] = useState({
    tahun: new Date().getFullYear(), bulan: BULAN[new Date().getMonth()], minggu: 1,
    kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "", catatan: "",
  });
  const [c, setC] = useState({});
  const [busy, setBusy] = useState(false);
  const [products, setProducts] = useState([]);

  const load = () => http.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const total = Object.values(c).reduce((a, b) => a + (Number(b) || 0), 0);

  const autofill = async (kode) => {
    if (!kode) return;
    try {
      const { data } = await http.get(`/products/${kode.toUpperCase()}`);
      setF((s) => ({ ...s, deskripsi_produk: data.deskripsi_produk || "", type_produk: data.type_produk || "",
        spesifikasi_produk: data.spesifikasi_produk || "" }));
      toast.success(`Data produk ${kode.toUpperCase()} terisi otomatis`);
    } catch {}
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/sales", {
        ...f, tahun: Number(f.tahun), minggu: Number(f.minggu),
        kode_stock: f.kode_stock.toUpperCase(),
        cabang: Object.fromEntries(BRANCHES.map((b) => [b, Number(c[b]) || 0])),
      });
      toast.success(`Tersimpan. Total penjualan nasional: ${nf(data.total_penjualan)}`);
      setC({}); setF({ ...f, kode_stock: "", deskripsi_produk: "", type_produk: "", spesifikasi_produk: "" }); load();
    } catch (e2) {
      toast.error(apiError(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const inp = "w-full h-10 px-3 border border-neutral-300 bg-white outline-none focus:ring-2 focus:ring-[#4338CA] focus:border-transparent text-sm";
  const lbl = "text-[11px] font-semibold uppercase tracking-wide text-[#525252]";

  return (
    <div data-testid="sales-entry-page">
      <PageHeader subtitle="Dept Sales & Marketing · Kantor Pusat" title="Entry & Upload Data Penjualan Mingguan" testId="sales-entry-header" />
      <div className="p-6 sm:p-8 space-y-4">
        <UploadBox endpoint="/sales/upload" templateKind="sales" onDone={load} testId="sales-upload" />

        <form onSubmit={submit} className="erp-panel p-5 space-y-5" data-testid="sales-entry-form">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252]">Entry Manual Penjualan per Minggu</div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className={lbl}>Tahun</label>
              <input data-testid="sales-tahun-input" type="number" className={inp + " mono"} value={f.tahun}
                onChange={(e) => setF({ ...f, tahun: e.target.value })} />
            </div>
            <div>
              <label className={lbl}>Bulan</label>
              <select data-testid="sales-bulan-select" className={inp} value={f.bulan}
                onChange={(e) => setF({ ...f, bulan: e.target.value })}>
                {BULAN.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Minggu</label>
              <select data-testid="sales-minggu-select" className={inp} value={f.minggu}
                onChange={(e) => setF({ ...f, minggu: e.target.value })}>
                {[1, 2, 3, 4, 5].map((m) => <option key={m} value={m}>Minggu {m}</option>)}
              </select>
            </div>
            <div>
              <label className={lbl}>Kode Stock</label>
              <input data-testid="sales-kode-input" required list="prodlist4" className={inp + " uppercase"}
                value={f.kode_stock} onChange={(e) => setF({ ...f, kode_stock: e.target.value })}
                onBlur={(e) => autofill(e.target.value)} placeholder="KSFO022" />
              <datalist id="prodlist4">
                {products.map((p) => <option key={p.kode_stock} value={p.kode_stock}>{p.deskripsi_produk}</option>)}
              </datalist>
            </div>
            <div>
              <label className={lbl}>Deskripsi Produk</label>
              <input data-testid="sales-deskripsi-input" className={inp} value={f.deskripsi_produk}
                onChange={(e) => setF({ ...f, deskripsi_produk: e.target.value })} />
            </div>
            <div>
              <label className={lbl}>Type Produk</label>
              <input data-testid="sales-type-input" className={inp} value={f.type_produk}
                onChange={(e) => setF({ ...f, type_produk: e.target.value })} />
            </div>
            <div className="lg:col-span-2">
              <label className={lbl}>Spesifikasi Produk</label>
              <input data-testid="sales-spec-input" className={inp} value={f.spesifikasi_produk}
                onChange={(e) => setF({ ...f, spesifikasi_produk: e.target.value })} />
            </div>
          </div>

          <div>
            <div className={lbl + " mb-2"}>Qty Penjualan per Cabang</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3">
              {BRANCHES.map((b) => (
                <div key={b}>
                  <label className="text-xs text-[#525252]">{b}</label>
                  <input data-testid={`sales-${b.toLowerCase()}-input`} type="number" className={inp + " mono text-right"}
                    value={c[b] ?? ""} onChange={(e) => setC({ ...c, [b]: e.target.value })} placeholder="0" />
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 border-t border-neutral-200 pt-4">
            <div className="flex-1">
              <div className={lbl}>Rekap Total Penjualan Cabang</div>
              <div data-testid="sales-total-display" className="mono text-2xl font-semibold">{nf(total)}</div>
            </div>
            <input data-testid="sales-catatan-input" className={inp + " sm:max-w-xs"} placeholder="Catatan"
              value={f.catatan} onChange={(e) => setF({ ...f, catatan: e.target.value })} />
            <button data-testid="sales-submit-button" disabled={busy}
              className="h-10 px-6 bg-[#4338CA] text-white text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-[#3730A3] transition-colors disabled:opacity-60">
              <Save className="h-3.5 w-3.5" /> Simpan Penjualan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
